import { googleLogout } from '@react-oauth/google'
import { useEffect, useRef } from 'react'
import { useDispatch } from 'react-redux'
import { useNavigate, useLocation } from 'react-router-dom'
import { authActions } from '../auth/slices/auth.slice'
import useDeleteToken from './useDeleteToken'
import ROUTES from '../route/routes'


/**
 * Custom hook to handle inactive user sessions. Monitors user activity and logs out the user
 * if inactivity exceeds the specified timeout duration.
 *
 * @param {number} [timeoutDuration=900000] - The duration of inactivity (in milliseconds) 
 *                                            after which the user session is considered inactive. 
 *                                            Defaults to 15 minutes (900000 ms).
 * @returns {Object} - An object containing methods to interact with the inactivity handler.
 * @returns {Function} resetTimer - Resets the inactivity timer to the current time.
 *
 * @example
 * const { resetTimer } = useInactiveSessionHandler();
 * 
 * // Reset the inactivity timer manually
 * resetTimer();
 */

const useInactiveSessionHandler = (timeoutDuration = 15 * 60 * 1000) => {
  const lastActivityTimeRef = useRef(Date.now())
  const navigate = useNavigate()
  const location = useLocation()
  const deleteToken = useDeleteToken()
  const dispatch = useDispatch()

  useEffect(() => {
    const updateActivity = () => {
      lastActivityTimeRef.current = Date.now()
    }

    const checkInactivity = async () => {
      if (Date.now() - lastActivityTimeRef.current > timeoutDuration) {
        try {
          googleLogout()
          // Try to delete token, but logout regardless of result
          try {
            const deleteRes = await deleteToken()
            if (deleteRes.status !== 200) {
              console.warn('Token deletion failed, proceeding to logout.')
            }
          } catch (deleteErr) {
            console.warn('Token deletion error:', deleteErr)
          }
          dispatch(authActions.logout())
          navigate(ROUTES.PUBLIC.SESSION_EXPIRED, { state: { from: location }, replace: true })
        } catch (logoutError) {
          console.error('Error during logout:', logoutError)
        }
      }
    }

    // Add event listeners for user activity
    window.addEventListener('mousemove', updateActivity)
    window.addEventListener('keydown', updateActivity)
    window.addEventListener('click', updateActivity)
    window.addEventListener('touchstart', updateActivity)

    const interval = setInterval(checkInactivity, 1000)

    return () => {
      window.removeEventListener('mousemove', updateActivity)
      window.removeEventListener('keydown', updateActivity)
      window.removeEventListener('click', updateActivity)
      window.removeEventListener('touchstart', updateActivity)
      clearInterval(interval)
    }
  }, [timeoutDuration, navigate, deleteToken, dispatch, location])

  return {
    resetTimer: () => {
      lastActivityTimeRef.current = Date.now()
    }
  }
}

export default useInactiveSessionHandler
